import React, { useEffect, useState } from "react"
import Pagination from "../../components/Pagination"
import { FiPlusCircle, FiEye } from "react-icons/fi"
import { SuccessNotification } from "../../utils"
import { Redirect, useLocation } from "react-router-dom"
import DashboardHeader from "../../components/DashboardHeader"
import { Tooltip } from "../../layout/sidebar"
import { IoArrowBack, IoArrowForward } from "react-icons/io5"
import EmptyHarvestRequest from "../../components/NoData/EmptyHarvestRequest"
import AddHarvestRequestModal from "../../components/modals/AddHarvestRequestModal"
import HarvestDetailDrawer from "../../components/Drawers/HarvestDetailDrawer"
import HarvestRequestFilter from "../../components/Filters/HarvestRequestFilter"
import Spinner from "../../components/Spinner"
import useSWR from "swr"
import api, { BASE_URL, fetchAll, fetcher } from "../../utils/api";
import { RxPencil1 } from "react-icons/rx"
import EditHarvestRequestModal from '../../components/modals/EditHarvestRequestModal'
import DeleteModal from "../../components/DeleteModal"
import { useHistory } from "react-router-dom";
import { formatHarvestPriceWeightSummary } from "../../utils/harvestRequest"

const PAGE_SIZE = 10

const Index = () => {
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1)
  const [selected, setSelected] = useState(null)
  const [showDetailDrawer, setShowDetailDrawer] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [q, setQ] = useState("")
  const [states, setState] = useState({ status: "", request_type: "", date: "" })
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection"
    }
  ])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { data: harvestRequest, isLoading, mutate } = useSWR(
    `harvest-requests/`,
    () => fetchAll(`harvest-requests/`)
  )

  // --- Filtering and Search Logic ---
  const getFilteredHarvests = () => {
    let data = harvestRequest || []
    // Filter by status, request_type
    if (states.status && states.status !== "") {
      data = data.filter(
        (el) => el.request_status?.toLowerCase() === states.status
      )
    }
    if (states.request_type && states.request_type !== "") {
      data = data.filter(
        (el) => el.request_type?.toLowerCase() === states.request_type
      )
    }
    // Date range filter
    const startDate = dateRange[0]?.startDate
    const endDate = dateRange[0]?.endDate
    if (startDate && endDate) {
      data = data.filter(
        (el) => {
          const created = new Date(el.created_at)
          return created >= startDate && created <= endDate
        }
      )
    }
    // Search by owner name, request_id, etc.
    if (q) {
      const query = q.toLowerCase()
      data = data.filter(
        (el) =>
          el.request_id?.toLowerCase().includes(query) ||
          `${el.owner?.firstname} ${el.owner?.lastname}`.toLowerCase().includes(query) ||
          el.owner?.email?.toLowerCase().includes(query) ||
          el.quantity?.toString().includes(query) ||
          el.price?.toString().includes(query) ||
          el.weight_in_tons?.toString().includes(query) ||
          el.price_per_ton?.toString().includes(query) ||
          el.est_no_of_bunches?.toString().includes(query) ||
          el.request_status?.toLowerCase().includes(query) ||
          el.response_status?.toLowerCase().includes(query)
      )
    }
    return data
  }

  // --- Pagination Logic ---
  const filteredHarvests = getFilteredHarvests()
  const totalPages = Math.ceil(filteredHarvests.length / PAGE_SIZE)
  const paginatedHarvests = filteredHarvests.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [filteredHarvests.length])

  // --- Pagination Handlers ---
  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }
  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }
  const handlePageClick = (page) => {
    setCurrentPage(page)
  }

  const handleNewGroup = () => {
    setShowAddModal(true)
  }

  const reset = () => {
    setQ("")
    setState({ status: "", request_type: "", date: "" })
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection"
      }
    ])
    setCurrentPage(1)
  }

  function handleChange(evt) {
    const value = evt.target.value
    setState({
      ...states,
      [evt.target.name]: value,
    })
    setCurrentPage(1)
  }

  // --- Suggestions for Search ---
  const suggestions = (harvestRequest || [])
    .flatMap(h => [h.request_id, `${h.owner?.firstname} ${h.owner?.lastname}`])
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i)

  const { state } = useLocation()
  const user = JSON.parse(localStorage.getItem("user"))
  if (user?.is_firsttime) {
    return <Redirect to={state?.from || "/admin/dashboard"} />
  }

  // Delete handler
  const handleDelete = async () => {
    if (!selected?.id) return
    setDeleteLoading(true)
    try {
      const token = localStorage.getItem('token')
      await fetch(`${BASE_URL}harvest-requests/${selected.id}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      setShowDeleteModal(false)
      setDeleteLoading(false)
      mutate()
      setSelected(null)
      SuccessNotification('Harvest request deleted!')
    } catch (err) {
      setDeleteLoading(false)
      setShowDeleteModal(false)
      setSelected(null)
      SuccessNotification(err?.message || 'Failed to delete harvest request')
    }
  }

  return (
    <>
      {showAddModal && <AddHarvestRequestModal
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        onClose={() => setShowAddModal(false)}
        mutate={mutate}
      />}

      {showEditModal && (
        <EditHarvestRequestModal
          show={showEditModal}
          setShow={setShowEditModal}
          harvestId={editId}
          mutate={mutate}
        />
      )}

      {showDeleteModal && <DeleteModal
        handleDelete={handleDelete}
        loading={deleteLoading}
        setShowConfirmModal={setShowDeleteModal}
        showConfirmModal={showDeleteModal}
        title={`request ${selected?.request_id}`}
      />}

      <HarvestDetailDrawer
        showDetailDrawer={showDetailDrawer}
        setShowDetailDrawer={setShowDetailDrawer}
        onClose={() => setShowDetailDrawer(false)}
        selected={selected}
      />

      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          {/* DASHBOARD HEADER */}
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Requests" />
            <div className="flex flex-col md:flex-row gap-2">
              <button
                onClick={handleNewGroup}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md"
              >
                <FiPlusCircle size={20} />
                Create Request
              </button>
            </div>
          </div>

          <HarvestRequestFilter
            placeholder="Search request"
            suggestions={suggestions}
            onSearch={setQ}
            searchValue={q}
            setSearchValue={setQ}
            states={states}
            setStates={setState}
            handleChange={handleChange}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />

          {(q || states.status || states.request_type || (dateRange[0]?.startDate && dateRange[0]?.endDate)) && (
            <div className="flex justify-end">
              <button
                onClick={reset}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
              >
                Reset Filters
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="flex items-center mb-1"></div>
              <div className="my-5">
                {filteredHarvests.length > 0 ? (
                  <>
                    <div className="my-5">
                      <div className="flex flex-col">
                        <div className="-m-1.5 overflow-x-auto">
                          <div className="p-1.5 min-w-full inline-block align-middle">
                              <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                                  <thead className="bg-transparent dark:bg-secondaryDark">
                                  <tr>
                                      <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Request Id</th>
                                      <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Created By</th>
                                      <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Price / Quantity</th>
                                      <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Number of Groups</th>
                                      <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Request Type</th>
                                      <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Status</th>
                                    <th className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize hover:text-black ">Action</th>
                                  </tr>
                                </thead>
                                  <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                                  {paginatedHarvests.map((data, index) => (
                                    <tr key={data.id} className="text-[13px]">
                                      <td className="px-6 py-4 whitespace-nowrap">{data?.request_id}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">{`${data?.owner?.firstname} ${data?.owner?.lastname}`}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {formatHarvestPriceWeightSummary(data)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {data?.target_groups_count}
                                      </td>
                                    
                                      <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {/* Status badge */}
                                        <span
                                          className={`capitalize px-3 py-1 rounded-md text-xs font-semibold 
                                            ${data?.request_status?.toLowerCase() === 'published' ? 'bg-yellow-50 text-yellowcolor' :
                                              data?.request_status?.toLowerCase() === 'responded' || data?.request_status?.toLowerCase() === 'fulfilled' || data?.request_status?.toLowerCase() === 'received' ? 'bg-green-50 text-green-700' :
                                                data?.request_status?.toLowerCase() === 'pending' ? 'bg-blue-50 text-blue-700' :
                                                  data?.request_status?.toLowerCase() === 'rejected' ? 'bg-red-50 text-red-500' :
                                                    'bg-gray-50 text-gray-500'}
                                          `}
                                        >
                                          {data?.request_status}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {/* Response status badge */}
                                        <span
                                          className={`capitalize px-3 py-1 rounded-md text-xs font-semibold 
                                            ${data?.response_status?.toLowerCase() === 'published' ? 'bg-yellow-50 text-yellowcolor' :
                                              data?.response_status?.toLowerCase() === 'responded' || data?.response_status?.toLowerCase() === 'fulfilled' || data?.response_status?.toLowerCase() === 'received' ? 'bg-green-50 text-green-700' :
                                                data?.response_status?.toLowerCase() === 'pending' ? 'bg-blue-50 text-blue-700' :
                                                  data?.response_status?.toLowerCase() === 'rejected' ? 'bg-red-50 text-red-500' :
                                                    'bg-gray-50 text-gray-500'}
                                          `}
                                        >
                                          {data?.response_status}
                                        </span>
                                      </td>

                                      <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                        <div className="flex flex-row items-center gap-4">
                                          <Tooltip text="View" position="top">
                                            <button
                                              onClick={() => {
                                                // setSelected(data)
                                                // setShowDetailDrawer(true)
                                                history.push(`/admin/harvest-requests/${data.id}`, { state: data })
                                              }}
                                              className=""
                                            >
                                              <FiEye size={16} />
                                            </button>
                                          </Tooltip>
                                          <Tooltip text="Edit" position="top">
                                            <button onClick={() => {
                                              setEditId(data.id)
                                              setShowEditModal(true)
                                            }}>
                                              <RxPencil1 size={16} />
                                            </button>
                                          </Tooltip>

                                          <Tooltip text="Delete" position="top">
                                            <button onClick={() => {
                                              setSelected(data)
                                              setShowDeleteModal(true)
                                            }}>
                                              <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            </button>
                                          </Tooltip>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                                <Pagination
                                  currentPage={currentPage}
                                  totalPages={totalPages}
                                  onPageChange={handlePageClick}
                                />
                             </div>
                           </div>
                         </div>
                       </div>
                     </div>
                   </>
                 ) : (
                   <EmptyHarvestRequest
                     handleNewGroup={handleNewGroup}
                   />
                 )}
               </div>
               </>
          )}
        </div>
      </div>
    </>
  )
}

export default Index